import { encode } from '@mapbox/polyline';
import { Button, Col, Collapse, Form, message, Modal, Popover, Row, Spin, Steps, Table, Tag } from 'antd';
import copy from 'copy-to-clipboard';
import dayjs from 'dayjs';
import moment from 'moment';
import React, { useEffect, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';
import { useNavigate, useParams } from 'react-router-dom';
import { ReactSVG } from 'react-svg';

// Components
import DeliveryForm from '../components/Forms/OrdersDetail/DeliveryForm';
import OrderInfoForm from '../components/Forms/OrdersDetail/OrderInfoForm';
import PickupForm from '../components/Forms/OrdersDetail/PickupForm';
import GoogleMap from '../components/Map/GoogleMap';
import Icon from '../components/Markers/icons/pickup';
import BreadCrumb from '../modules/breadcrumbs';

// Deps
import {
  DAY_FORMAT,
  DAYTIME_FORMAT,
  ISSUE_TYPES,
  ORDER_STATUSES_DISPLAY,
  ORDER_TYPES,
  ORDER_TYPES_DISPLAY,
  TIME_FORMAT,
} from '../constants';
import { doGetOrderTickets } from '../modules/order-tickets/stores/thunk';
import { doGetOrder, doUpdateOrder } from '../modules/orders/stores/thunk';
import { doGetPartners } from '../modules/partners/stores/thunk';
import { selectAllPartners } from '../modules/partners/stores/slice';
import * as partnerApi from '../modules/partners/api';
import { getProgressStatus, imageMaker } from '../utils/common';
import request from '../utils/request';
import useColumnConfig from '../hooks/useColumnConfig';
import ColumnConfigDrawer from '../components/ColumnConfigDrawer';
import './OrderDetail.scss';

const typePage = {
  order: {
    type: 'orderInfo',
    title: 'Edit Order Info',
  },
  pickup: {
    type: 'pickupInfo',
    title: 'Edit Pickup',
  },
  delivery: {
    type: 'deliveryInfo',
    title: 'Edit Delivery',
  },
};

const OrderDetailPage = () => {
  const [form] = Form.useForm();
  const { id } = useParams();
  const navigate = useNavigate();
  const dispatch = useDispatch();
  const order = useSelector((state) => state.order.currentOrder);
  const orderTickets = useSelector((state) => state.orderTickets.orderTickets);

  const isFetchingOrder = useSelector((state) => state.order.isFetching);
  const status = useSelector((state) => state.order.status);
  const action = useSelector((state) => state.order.action);
  const user = useSelector((state) => state.auth.user);
  const partners = useSelector(selectAllPartners);

  const isFetching = isFetchingOrder;
  const [messageApi, contextHolder] = message.useMessage();
  const role = user?.role?.toLowerCase();
  const isPartnerViewer = role === 'partner_viewer';
  const breadCrumbs = isPartnerViewer
    ? [{ text: 'ORDERS', url: '/partner/orders' }, { text: order?.orderCode }]
    : [{ text: 'ORDERS', url: '/orders' }, { text: order?.orderCode }];

  const [titleModal, setTitleModal] = useState('');
  const [actionPage, setActionPage] = useState('');
  const [isModalOpen, setIsModalOpen] = useState(false);
  const [markers, setMarkers] = useState([]);
  const [polylines, setPolylines] = useState([]);
  const [types, setTypes] = useState(order?.types || []);

  // Webhook logs
  const [webhookLogs, setWebhookLogs] = useState([]);
  const [webhookLogsOpen, setWebhookLogsOpen] = useState(false);
  const [webhookLogsLoaded, setWebhookLogsLoaded] = useState(false);
  const [retryingLogId, setRetryingLogId] = useState(null);

  const onClickCopy = (value) => {
    copy(value);
    messageApi.open({
      type: 'success',
      style: {
        marginTop: '68px',
      },
      content: 'Copied',
    });
  };

  // Configurable field definitions for Order Info section
  const allFields = useMemo(() => {
    const fields = [
      {
        key: 'orderRef',
        title: 'Order Ref',
        render: (o) => (
          <div className="order-content hasCopy" onClick={() => onClickCopy(o.orderRef)}>
            {o.orderRef}
          </div>
        ),
      },
      {
        key: 'orderType',
        title: 'Order Type',
        render: (o) => (
          <div className="order-content">
            <span className={`status order ${o.orderType === ORDER_TYPES.SERVICE ? 'service' : ''}`}>
              {ORDER_TYPES_DISPLAY[o.orderType] || 'Standard'}
            </span>
          </div>
        ),
      },
      {
        key: 'deliveryDate',
        title: 'Delivery Date',
        render: (o) => <div className="order-content">{moment(o.deliveryDate).format(DAY_FORMAT)}</div>,
      },
      {
        key: 'volume',
        title: 'Volume',
        render: (o) => <div className="order-content">{o.volume}</div>,
      },
      {
        key: 'types',
        title: 'Types',
        render: (o) => <div className="order-content">{o.types ? o.types.join(', ') : ''}</div>,
      },
      {
        key: 'name',
        title: 'Name',
        render: (o) => <div className="order-content">{o.name}</div>,
      },
      {
        key: 'description',
        title: 'Description',
        render: (o) => <div className="order-content">{o.description || '-'}</div>,
      },
      {
        key: 'size',
        title: 'Size (LxWxH)',
        render: (o) => {
          const parts = [o.sizeWidth, o.sizeLength, o.sizeHeight].filter(Boolean);
          return <div className="order-content">{parts.length ? parts.join(' x ') : '-'}</div>;
        },
      },
      {
        key: 'weight',
        title: 'Weight',
        render: (o) => <div className="order-content">{o.weight}</div>,
      },
      {
        key: 'partner',
        title: 'Partner',
        render: (o) => (
          <div className="order-content">
            {o.partnerRef?.id ? (
              <a href={`/admin/partners/${o.partnerRef.id}`} style={{ color: '#1677ff' }}>
                {o.partnerRef.name || o.partner || '-'}
              </a>
            ) : (
              o.partner || '-'
            )}
          </div>
        ),
      },
      ...(!isPartnerViewer
        ? [
            {
              key: 'partnerContact',
              title: 'Partner Contact',
              render: (o) => <div className="order-content">{o.partnerContact || '-'}</div>,
            },
          ]
        : []),
      {
        key: 'partnerOrderRef',
        title: 'Partner Order Ref',
        render: (o) => <div className="order-content">{o.partnerOrderRef || '-'}</div>,
      },
      {
        key: 'tourRef',
        title: 'Tour Ref',
        render: (o) => <div className="order-content">{o.tourRef || '-'}</div>,
      },
      {
        key: 'airtableRef',
        title: 'Airtable Order Record Ref',
        render: (o) => <div className="order-content">{o.airtableOrderRecordRef || '-'}</div>,
      },
      {
        key: 'preparationMode',
        title: 'Preparation Mode',
        render: (o) => <div className="order-content">{o.preparationMode || '-'}</div>,
      },
      {
        key: 'collectionForm',
        title: 'Collection Form',
        render: (o) => (
          <div className="order-content">
            {o.collectionForm ? (
              <a href={o.collectionForm} target="_blank" rel="noopener noreferrer">
                Click to view
              </a>
            ) : (
              '-'
            )}
          </div>
        ),
      },
      {
        key: 'issueType',
        title: 'Issue Type',
        render: (o) => (
          <div className="order-content">{o.issueType ? ISSUE_TYPES[o.issueType] || o.issueType : '-'}</div>
        ),
      },
      {
        key: 'routeId',
        title: 'Route',
        render: (o) =>
          o.routePlanId ? (
            <div className="order-content">
              <a href={`/routes/${o.routePlan?.slug}`} style={{ color: '#1677ff' }}>
                #{o.routePlanId}
              </a>
            </div>
          ) : (
            <div className="order-content">-</div>
          ),
      },
    ];
    return fields;
  }, [isPartnerViewer]);

  const fieldConfig = useColumnConfig('order-detail-fields', allFields, []);

  useEffect(() => {
    setTypes(order?.types || []);
  }, [order?.types]);

  const fetchWebhookLogs = async () => {
    if (!order?.orderCode) return;
    const { response } = await partnerApi.getOrderWebhookLogs(order.orderCode);
    if (response) {
      setWebhookLogs(response);
    }
    setWebhookLogsLoaded(true);
  };

  const toggleWebhookLogs = () => {
    const nextOpen = !webhookLogsOpen;
    setWebhookLogsOpen(nextOpen);
    if (nextOpen && !webhookLogsLoaded) {
      fetchWebhookLogs();
    }
  };

  const handleRetryWebhook = async (logId) => {
    if (!order?.orderCode) return;
    setRetryingLogId(logId);
    try {
      const { error } = await partnerApi.retryWebhookLog(order.orderCode, logId);
      if (error) {
        messageApi.error('Retry failed');
      } else {
        messageApi.success('Webhook retried');
        await fetchWebhookLogs();
      }
    } catch {
      messageApi.error('Retry failed');
    } finally {
      setRetryingLogId(null);
    }
  };

  const logs = order?.logs || [];
  const statusDatetime = (status) => logs.find((l) => l.status === status)?.createdAt;

  const getPolylineAndMarkers = async () => {
    let newPolyline = [];
    const newMarkers = [
      {
        id: `${order.orderCode}-1`,
        content: <img src={imageMaker(Icon())} />,
        latitude: Number(order.pickupAddressLat),
        longitude: Number(order.pickupAddressLng),
        offsetX: 0,
        offsetY: 0,
        className: 'order-marker',
      },
      {
        id: `${order.orderCode}-2`,
        content: <img src="/icons/dropoff.svg" />,
        latitude: Number(order.deliveryAddressLat),
        longitude: Number(order.deliveryAddressLng),
        offsetX: 17,
        offsetY: -10,
        className: 'order-marker',
      },
    ];
    const code = encode([
      [order.pickupAddressLat, order.pickupAddressLng],
      [order.deliveryAddressLat, order.deliveryAddressLng],
    ]);
    const { response } = await request(`/common/polyline/${code}`, {
      method: 'GET',
    });
    if (response?.polyline) {
      newPolyline = [
        {
          id: code,
          polyline: response.polyline,
        },
      ];
    }
    setMarkers(newMarkers);
    setPolylines(newPolyline);
  };

  useEffect(() => {
    if (id) {
      setMarkers([]);
      setPolylines([]);
      dispatch(doGetOrder({ id }));
      dispatch(doGetOrderTickets());
      if (!isPartnerViewer) {
        dispatch(doGetPartners({ take: 1000000 }));
      }
    }
  }, [id]);

  useEffect(() => {
    if (
      order?.orderCode === id &&
      order?.pickupAddressLat &&
      order?.deliveryAddressLat &&
      order?.orderType !== ORDER_TYPES.SERVICE
    ) {
      getPolylineAndMarkers();
    }
  }, [order]);

  useEffect(() => {
    if (action === 'getOne' && ['succeeded', 'failed']?.includes(status) && !order) {
      navigate(isPartnerViewer ? '/partner/orders' : '/admin/orders');
    }
  }, [action, status, order]);

  useEffect(() => {
    if (action === 'update' && status === 'succeeded') {
      dispatch(doGetOrder({ id }));
      setIsModalOpen(false);
      setActionPage('');
    }
  }, [action, status]);

  const onClickEditInfo = () => {
    const ticketNames = order.tickets
      ?.map((ticketId) => orderTickets?.find((t) => t.id === ticketId)?.name)
      .filter((name) => !!name);
    form.setFieldsValue({
      orderType: order.orderType,
      orderRef: order.orderRef,
      status: order.status,
      deliveryDate: dayjs(order.deliveryDate),
      volume: order.volume,
      createdBy: order.createdBy,
      name: order.name,
      description: order.description,
      sizeLength: order.sizeLength,
      sizeWidth: order.sizeWidth,
      sizeHeight: order.sizeHeight,
      weight: order.weight,
      partnerId: order.partnerId || null,
      productDetailsKnown: order.productDetailsKnown !== false,
      orderProducts: (order.orderProducts || []).map((op) => ({
        partnerItemTypeId: op.partnerItemTypeId,
        quantity: op.quantity,
        notes: op.notes || '',
      })),
      partnerContact: order.partnerContact,
      partnerOrderRef: order.partnerOrderRef,
      tourRef: order.tourRef,
      airtableOrderRecordRef: order.airtableOrderRecordRef,
      preparationMode: order.preparationMode,
      collectionForm: order.collectionForm,
      ticketNames: ticketNames,
      issueType: order.issueType,
    });
    setActionPage(typePage.order.type);
    setTitleModal(typePage.order.title);
    setIsModalOpen(true);
  };

  const onClickEditPickup = () => {
    form.setFieldsValue({
      pickupName: order.pickupName,
      pickupEmail: order.pickupEmail,
      pickupAddress: {
        address: order.pickupAddress,
        latLng: {
          lat: order.pickupAddressLat,
          lng: order.pickupAddressLng,
        },
      },
      pickupPhone: order.pickupPhone,
      pickupDuration: order.pickupDuration,
      pickupNote: order.pickupNote,
      pickupTimeStart: order.pickupTimeStart ? dayjs(order.pickupTimeStart, TIME_FORMAT) : null,
      pickupTimeEnd: order.pickupTimeEnd ? dayjs(order.pickupTimeEnd, TIME_FORMAT) : null,
    });
    setActionPage(typePage.pickup.type);
    setTitleModal(typePage.pickup.title);
    setIsModalOpen(true);
  };

  const onClickEditDelivery = () => {
    const deliveryTimeStart = order.deliveryTimeStart ? dayjs(order.deliveryTimeStart, TIME_FORMAT) : null;
    const deliveryTimeEnd = order.deliveryTimeEnd ? dayjs(order.deliveryTimeEnd, TIME_FORMAT) : null;
    form.setFieldsValue({
      deliveryName: order.deliveryName,
      deliveryEmail: order.deliveryEmail,
      deliveryPhone: order.deliveryPhone,
      deliveryDuration: order.deliveryDuration,
      deliveryNote: order.deliveryNote,
      deliveryAddress: {
        address: order.deliveryAddress,
        latLng: {
          lat: order.deliveryAddressLat,
          lng: order.deliveryAddressLng,
        },
      },
      deliveryTimeStart,
      deliveryTimeEnd,
    });
    setActionPage(typePage.delivery.type);
    setTitleModal(typePage.delivery.title);
    setIsModalOpen(true);
  };

  const cancelModal = () => {
    setIsModalOpen(false);
    setActionPage(undefined);
  };

  const onFinishForm = (values) => {
    let params = {};
    if (actionPage === typePage.order.type) {
      params = {
        sizeWidth: values.sizeWidth ? Number(values.sizeWidth) : null,
        sizeLength: values.sizeLength ? Number(values.sizeLength) : null,
        sizeHeight: values.sizeHeight ? Number(values.sizeHeight) : null,
        weight: values.weight ? Number(values.weight) : null,
        orderRef: values.orderRef,
        // deliveryDate: values.deliveryDate.format(DATE_BACKEND_FORMAT),
        volume: values.volume ? Number(values.volume) : undefined,
        name: values.name,
        status: values.status,
        description: values.description,
        supportNote: values.supportNote,
        types,
        issueType: values.issueType,
        partnerId: values.partnerId || null,
        partner: values.partnerId ? partners.find((p) => p.id === values.partnerId)?.name || null : null,
        productDetailsKnown: values.productDetailsKnown !== false,
        orderProducts: values.orderProducts || [],
        partnerContact: values.partnerContact || null,
        partnerOrderRef: values.partnerOrderRef || null,
        tourRef: values.tourRef || null,
        airtableOrderRecordRef: values.airtableOrderRecordRef || null,
        preparationMode: values.preparationMode || null,
        collectionForm: values.collectionForm || null,
      };
    }
    if (actionPage === typePage.pickup.type) {
      params = {
        pickupName: values.pickupName,
        pickupAddress: values?.pickupAddress?.address,
        pickupAddressLat: Number(values?.pickupAddress?.latLng?.lat),
        pickupAddressLng: Number(values?.pickupAddress?.latLng?.lng),
        pickupPhone: values.pickupPhone,
        pickupEmail: values.pickupEmail || null,
        pickupNote: values.pickupNote,
        pickupDuration: values.pickupDuration ? Number(values.pickupDuration) : null,
        pickupTimeStart: values.pickupTimeStart ? values.pickupTimeStart.format(TIME_FORMAT) : null,
        pickupTimeEnd: values.pickupTimeEnd ? values.pickupTimeEnd.format(TIME_FORMAT) : null,
      };
    }
    if (actionPage === typePage.delivery.type) {
      params = {
        deliveryName: values.deliveryName,
        deliveryAddress: values?.deliveryAddress?.address,
        deliveryAddressLat: Number(values?.deliveryAddress?.latLng?.lat),
        deliveryAddressLng: Number(values?.deliveryAddress?.latLng?.lng),
        deliveryPhone: values.deliveryPhone,
        deliveryEmail: values.deliveryEmail || null,
        deliveryNote: values.deliveryNote,
        deliveryDuration: values.deliveryDuration ? Number(values.deliveryDuration) : null,
        deliveryTimeStart: values.deliveryTimeStart ? values.deliveryTimeStart.format(TIME_FORMAT) : null,
        deliveryTimeEnd: values.deliveryTimeEnd ? values.deliveryTimeEnd.format(TIME_FORMAT) : null,
      };
    }
    dispatch(doUpdateOrder({ id, ...params }))
      .unwrap()
      .then(() => {
        dispatch(doGetOrder({ id }));
        dispatch(doGetOrderTickets());
      })
      .catch((error) => {
        messageApi.open({
          type: 'error',
          content: error.message,
        });
      });
  };

  const goRoutePlanDetail = (order, type) => {
    navigate('/routes/' + order.routePlan?.slug + '?orderCode=' + order.id + '&type=' + type);
  };

  const onValuesChange = (changedValues, allValues) => {
    console.log({ changedValues, allValues });
  };

  const filePickups = order?.fileOrders?.filter((file) => file.type !== 'PICKUP_DONE') || [];
  const filePickupIds = filePickups.map((file) => file.id);
  const fileDeliveries = order?.fileOrders?.filter((file) => !filePickupIds.includes(file.id)) || [];

  const content = (
    <div className="actions">
      {order?.trackingToken && (
        <Button
          type="link"
          onClick={() => onClickCopy(window.location.origin + '/t/' + order.orderCode + '/' + order.trackingToken)}
        >
          Share Tracking URL
        </Button>
      )}
    </div>
  );

  if (!order) return <Spin style={{ width: '100vw', height: '100vh' }} spinning={isFetching}></Spin>;

  const itemsStatus = [
    {
      title: <div className="status order assigned">Assigned</div>,
      description: statusDatetime('ASSIGNED') && moment(statusDatetime('ASSIGNED')).format(DAYTIME_FORMAT),
    },
  ];

  if (order?.status === 'PICKUP_ARRIVED') {
    itemsStatus.push({
      title: (
        <div className={'status order ' + order?.status?.toLowerCase()}>{ORDER_STATUSES_DISPLAY[order?.status]}</div>
      ),
      description: statusDatetime('PICKUP_ARRIVED') && moment(statusDatetime('PICKUP_ARRIVED')).format(DAYTIME_FORMAT),
    });
  } else if (order?.status === 'PICKUP_FAILED') {
    itemsStatus.push({
      title: (
        <div className={'status order ' + order?.status?.toLowerCase()}>{ORDER_STATUSES_DISPLAY[order?.status]}</div>
      ),
      description: statusDatetime('PICKUP_ARRIVED') && moment(statusDatetime('PICKUP_ARRIVED')).format(DAYTIME_FORMAT),
    });
  }

  if (order.pickupStatus === 'COLLECTED') {
    itemsStatus.push({
      title: <div className="status order collected">Collected</div>,
      description: statusDatetime('COLLECTED') && moment(statusDatetime('COLLECTED')).format(DAYTIME_FORMAT),
    });

    itemsStatus.push({
      title: <div className="status order in_transit">In Transit</div>,
      description: statusDatetime('IN_TRANSIT') && moment(statusDatetime('IN_TRANSIT')).format(DAYTIME_FORMAT),
    });
  }

  if (order?.status === 'DELIVERY_FAILED') {
    itemsStatus.push({
      title: (
        <div className={'status order ' + order?.status?.toLowerCase()}>
          {ORDER_STATUSES_DISPLAY[order?.deliveryStatus]}
        </div>
      ),
      description:
        statusDatetime('DELIVERY_FAILED') && moment(statusDatetime('DELIVERY_FAILED')).format(DAYTIME_FORMAT),
    });
  } else if (order?.status === 'DELIVERY_ARRIVED') {
    itemsStatus.push({
      title: (
        <div className={'status order ' + order?.status?.toLowerCase()}>
          {ORDER_STATUSES_DISPLAY[order?.status?.replace('_', ' ')]}
        </div>
      ),
      description:
        statusDatetime('DELIVERY_ARRIVED') && moment(statusDatetime('DELIVERY_ARRIVED')).format(DAYTIME_FORMAT),
    });
  }

  if (order?.status === 'SKIPPED') {
    itemsStatus.push({
      title: <div className="status order skipped">Skipped</div>,
      description: statusDatetime('SKIPPED') && moment(statusDatetime('SKIPPED')).format(DAYTIME_FORMAT),
    });
  }

  itemsStatus.push({
    title: <div className="status order completed">Completed</div>,
    description: statusDatetime('COMPLETED') && moment(statusDatetime('COMPLETED')).format(DAYTIME_FORMAT),
  });

  return (
    <Spin spinning={isFetching}>
      <div className="OrderDetailPage Content">
        {contextHolder}
        <BreadCrumb breadCrumbs={breadCrumbs} />
        <Row gutter={[24, 24]}>
          <Col span={24}>
            <div className="OrderInfo white border-bottom">
              <div className="OrderInfoHeader">
                <div className="left">
                  <div className="orderId" onClick={() => onClickCopy(order.orderId)}>
                    {order.orderCode}
                    <img src="/icons/file_copy.svg" />
                  </div>
                </div>
                <div className="right">
                  <div className="createdInfo">
                    Created {!!order.createdBy && ' From '}
                    {!!order.createdBy && <b>{order.createdBy}</b>} at{' '}
                    <b>{moment(order.createdAt).format(DAYTIME_FORMAT)}</b>
                  </div>
                  <div className="headerActions">
                    {!isPartnerViewer && (
                      <Button
                        className="ButtonPrimary orderButton"
                        type="primary"
                        size="small"
                        onClick={onClickEditInfo}
                      >
                        Edit
                        <ReactSVG className="icon" src="/icons/edit.svg" />
                      </Button>
                    )}
                    {order?.trackingToken && (
                      <Popover
                        overlayClassName="share-content-order"
                        placement="bottomLeft"
                        content={content}
                        trigger="click"
                      >
                        <Button className="ButtonWhite orderButton" size="small">
                          <ReactSVG className="icon upload" src="/icons/share.svg" />
                        </Button>
                      </Popover>
                    )}
                    <Button type="text" size="small" className="settingsButton" onClick={fieldConfig.openDrawer}>
                      <ReactSVG className="icon" src="/icons/settings.svg" />
                    </Button>
                  </div>
                </div>
              </div>
              <div className="OrderInfoContent">
                <Row gutter={[24, 16]}>
                  {fieldConfig.visibleColumns.map((field) => (
                    <Col span={6} key={field.key}>
                      <label>{field.title}</label>
                      {field.render(order)}
                    </Col>
                  ))}
                </Row>
              </div>
            </div>
            <div className="statuses-log white border-bottom">
              <div className="header">
                <div className="Info Rider">
                  <label>Rider: </label>
                  <div className="Text">{order.rider?.user?.name || 'Unassigned'}</div>
                </div>
                <div className="button-history">
                  {/* <Button size="small">
                    History Log
                    <ReactSVG className="icon" src="/icons/history.svg" />
                  </Button> */}
                </div>
              </div>
              <div className="statuses">
                <Steps current={getProgressStatus(order.status)} labelPlacement="vertical" items={itemsStatus} />
              </div>
            </div>
            <div className="super-note white">
              <p>
                <b>Support Note:</b> -
              </p>
            </div>
          </Col>
          <Col span={12}>
            <div className="PartnerInfo Pickup white">
              <div className="section-title">Pickup</div>
              <Row gutter={[24, 16]}>
                <Col span={12}>
                  <div className="header left">
                    <div className="text">
                      <h5>{order?.pickupName || '-'}</h5>
                      <span>{order?.pickupEmail || '-'}</span>
                    </div>
                  </div>
                </Col>
                <Col span={12}>
                  <div className="actions header right">
                    {!isPartnerViewer && (
                      <Button type="link" size="small" onClick={onClickEditPickup}>
                        Edit
                        <ReactSVG className="icon" src="/icons/edit.svg" />
                      </Button>
                    )}
                    {order.routePlanId && (
                      <Button
                        type="link"
                        size="small"
                        disabled={!order.routePlanId}
                        onClick={() => goRoutePlanDetail(order, STEP_TYPE.pickup)}
                      >
                        View on Route Plan
                        <ReactSVG className="icon" src="/icons/routePlan.svg" />
                      </Button>
                    )}
                  </div>
                </Col>
                <Col span={24}>
                  <label>Address</label>
                  <div className="order-content hasCopy" onClick={() => onClickCopy(order.pickupAddress)}>
                    {order.pickupAddress}
                    <img src="/icons/file_copy.svg" />
                  </div>
                </Col>
                <Col span={8}>
                  <label>Phone Number</label>
                  <div className="order-content">{order.pickupPhone}</div>
                </Col>
                <Col span={8}>
                  <label>Pickup Time Window</label>
                  <div className="order-content">
                    {order.pickupTimeStart || ''} - {order.pickupTimeEnd || ''}
                  </div>
                </Col>
                <Col span={8}>
                  <div>
                    <label>Duration</label>
                    <div className="order-content">
                      {order.pickupDuration
                        ? order.pickupDuration + (order.pickupDuration > 1 ? ' minutes' : ' minute')
                        : ''}
                    </div>
                  </div>
                </Col>
                <Col span={8}>
                  <label>Note</label>
                  <div className="order-content">{order.pickupNote || '-'}</div>
                </Col>
              </Row>
              <Row gutter={[24, 16]}>
                <Col span={24}>
                  <label>POP</label>
                  <div className="order-content images">
                    {filePickups.map((file) => (
                      <div key={file.id}>
                        <img src={user.host + '/' + file.file.key} target="_blank" rel="noopener noreferrer" />
                      </div>
                    ))}
                  </div>
                </Col>
              </Row>
            </div>
          </Col>
          {order.orderType !== ORDER_TYPES.SERVICE && (
            <Col span={12}>
              <div className="PartnerInfo Delivery white">
                <div className="section-title">Delivery</div>
                <Row gutter={[24, 16]}>
                  <Col span={12}>
                    <div className="header left">
                      <div className="text">
                        <h5>{order?.deliveryName || '-'}</h5>
                        <span>{order?.deliveryEmail || '-'}</span>
                      </div>
                    </div>
                  </Col>
                  <Col span={12}>
                    <div className="actions header right">
                      {!isPartnerViewer && !order.routePlanId && (
                        <Button type="link" size="small" onClick={onClickEditDelivery}>
                          Edit
                          <ReactSVG className="icon" src="/icons/edit.svg" />
                        </Button>
                      )}
                      {order.routePlanId && (
                        <Button
                          type="link"
                          size="small"
                          disabled={!order.routePlanId}
                          onClick={() => goRoutePlanDetail(order, STEP_TYPE.delivery)}
                        >
                          View on Route Plan
                          <ReactSVG className="icon" src="/icons/routePlan.svg" />
                        </Button>
                      )}
                    </div>
                  </Col>
                  <Col span={24}>
                    <label>Address</label>
                    <div className="order-content hasCopy" onClick={() => onClickCopy(order.deliveryAddress)}>
                      {order.deliveryAddress}
                      <img src="/icons/file_copy.svg" />
                    </div>
                  </Col>
                  <Col span={8}>
                    <label>Phone Number</label>
                    <div className="order-content">{order.deliveryPhone}</div>
                  </Col>
                  <Col span={8}>
                    <label>Delivery Time Window</label>
                    <div className="order-content">
                      {order.deliveryTimeStart || ''} - {order.deliveryTimeEnd || ''}
                    </div>
                  </Col>
                  <Col span={8}>
                    <div>
                      <label>Duration</label>
                      <div className="order-content">
                        {order.deliveryDuration
                          ? order.deliveryDuration + (order.deliveryDuration > 1 ? ' minutes' : ' minute')
                          : '-'}
                      </div>
                    </div>
                  </Col>
                  <Col span={8}>
                    <label>Note</label>
                    <div className="order-content">{order.deliveryNote || '-'}</div>
                  </Col>
                </Row>
                <Row gutter={[24, 16]}>
                  <Col span={24}>
                    <label>POD</label>
                    <div className="order-content images">
                      {fileDeliveries.map((file) => (
                        <div key={file.id}>
                          <img src={user.host + '/' + file.file.key} target="_blank" rel="noopener noreferrer" />
                        </div>
                      ))}
                    </div>
                  </Col>
                </Row>
                {order?.trackingToken && (
                  <Row gutter={[24, 16]} style={{ marginTop: 8 }}>
                    <Col span={24}>
                      <label>Tracking URL</label>
                      <div className="order-content">
                        <a
                          href={`${window.location.origin}/t/${order.orderCode}/${order.trackingToken}`}
                          target="_blank"
                          rel="noopener noreferrer"
                        >
                          {`${window.location.origin}/t/${order.orderCode}/${order.trackingToken}`}
                        </a>
                      </div>
                    </Col>
                  </Row>
                )}
              </div>
            </Col>
          )}
          <Col span={12}>
            <div className="wrapper-map">
              <GoogleMap markers={markers} polylines={polylines} className="mapbox-order-detail" />
            </div>
          </Col>
          <Col span={12}>
            <div className="DeliveryDetail white">
              <Row>
                {/* <Col span={24}>
                  <div className="wrapper border-bottom">
                    <div className="title">Delivery Status</div>
                  </div>
                </Col> */}
                <Col span={24}>
                  <div className="wrapper">
                    <div className="title">Delivery Note</div>
                    <div className="content">{order.packageWas || '-'}</div>
                  </div>
                </Col>
                <Col span={24}>
                  <div className="wrapper">
                    <div className="title">Pickup Note</div>
                    <div className="content">{order.pickupMobileNote || '-'}</div>
                  </div>
                </Col>
                <Col span={24}>
                  <div className="wrapper border-top">
                    <div className="title">Rider Note</div>
                    <div className="content">{order.customerNote || '-'}</div>
                  </div>
                </Col>
                {order.issueType && (
                  <Col span={24}>
                    <div className="wrapper border-top driver-report-section">
                      <div className="title">Driver Report</div>
                      <div className="content">
                        <div className="driver-report-issue">
                          <span className="driver-report-label">Issue:</span>{' '}
                          {ISSUE_TYPES[order.issueType] || order.issueType}
                        </div>
                        {order.customerNote && (
                          <div className="driver-report-note">
                            <span className="driver-report-label">Note:</span> {order.customerNote}
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                )}
                {order.scannedCode && (
                  <Col span={24}>
                    <div className="wrapper border-top scanned-code-section">
                      <div className="title">Scanned Code</div>
                      <div className="content">
                        <span className="scanned-code-value">{order.scannedCode}</span>
                        {order.scannedCodeType && <span className="scanned-code-type">({order.scannedCodeType})</span>}
                        {order.scannedAt && (
                          <div className="scanned-code-time">
                            Scanned at: {moment(order.scannedAt).format(DAYTIME_FORMAT)}
                          </div>
                        )}
                      </div>
                    </div>
                  </Col>
                )}
                {order.collectedItems?.length > 0 && (
                  <Col span={24}>
                    <div className="wrapper border-top collected-items-section">
                      <div className="title">Collected Items ({order.collectedItems.length})</div>
                      <div className="content">
                        <div className="collected-items-list">
                          {order.collectedItems.map((item) => (
                            <div key={item.id} className="collected-item-row">
                              <span className="collected-item-name">
                                {item.partnerItemType?.name || item.description || 'Item'}
                              </span>
                              {item.scannedCode && <span className="collected-item-code">{item.scannedCode}</span>}
                              {item.weight != null && (
                                <span className="collected-item-weight">
                                  {item.weight} {item.partnerItemType?.unitOfMeasure || 'kg'}
                                </span>
                              )}
                              {item.quantity > 1 && <span className="collected-item-qty">x{item.quantity}</span>}
                              {item.partnerItemType?.category && (
                                <span
                                  className={`collected-item-category ${item.partnerItemType.category.toLowerCase()}`}
                                >
                                  {item.partnerItemType.category}
                                </span>
                              )}
                            </div>
                          ))}
                        </div>
                      </div>
                    </div>
                  </Col>
                )}
                {(order.partnerId || order.partner) && (
                  <Col span={24}>
                    <div className="wrapper border-top webhook-logs-section">
                      <div className="title clickable" onClick={toggleWebhookLogs}>
                        Webhook Logs {webhookLogsLoaded && `(${webhookLogs.length})`}
                        <span className={`expand-icon ${webhookLogsOpen ? 'open' : ''}`}>&#9660;</span>
                      </div>
                      {webhookLogsOpen && (
                        <div className="content">
                          {webhookLogs.length === 0 && webhookLogsLoaded && (
                            <div className="no-logs">No webhook calls recorded</div>
                          )}
                          {webhookLogs.length > 0 && (
                            <Table
                              dataSource={webhookLogs}
                              rowKey="id"
                              size="small"
                              pagination={false}
                              className="webhook-logs-table"
                              expandable={{
                                expandedRowRender: (log) => (
                                  <div className="webhook-log-detail">
                                    <div className="webhook-detail-section">
                                      <div className="webhook-detail-label">Request Body</div>
                                      <pre className="webhook-body">{JSON.stringify(log.requestBody, null, 2)}</pre>
                                    </div>
                                    <div className="webhook-detail-section">
                                      <div className="webhook-detail-label">Response</div>
                                      <pre className="webhook-body">
                                        {log.errorMessage
                                          ? `Error: ${log.errorMessage}`
                                          : typeof log.responseBody === 'string' && log.responseBody.startsWith('{')
                                            ? JSON.stringify(JSON.parse(log.responseBody), null, 2)
                                            : log.responseBody || '(empty)'}
                                      </pre>
                                    </div>
                                  </div>
                                ),
                              }}
                              columns={[
                                {
                                  title: 'Status',
                                  dataIndex: 'responseStatus',
                                  key: 'status',
                                  width: 70,
                                  render: (status) => (
                                    <Tag color={status >= 200 && status < 300 ? 'green' : status ? 'red' : 'orange'}>
                                      {status || 'Err'}
                                    </Tag>
                                  ),
                                },
                                {
                                  title: 'Event',
                                  dataIndex: 'eventType',
                                  key: 'event',
                                  width: 140,
                                  render: (val) => <span style={{ fontWeight: 500 }}>{val}</span>,
                                },
                                {
                                  title: 'Time',
                                  dataIndex: 'createdAt',
                                  key: 'time',
                                  width: 160,
                                  render: (val) => moment(val).format('DD/MM/YYYY HH:mm:ss'),
                                },
                                {
                                  title: 'Duration',
                                  dataIndex: 'durationMs',
                                  key: 'duration',
                                  width: 80,
                                  render: (val) => (val != null ? `${val}ms` : '-'),
                                },
                                ...(!isPartnerViewer
                                  ? [
                                      {
                                        title: '',
                                        key: 'action',
                                        width: 70,
                                        render: (_, log) => (
                                          <Button
                                            type="link"
                                            size="small"
                                            loading={retryingLogId === log.id}
                                            onClick={(e) => {
                                              e.stopPropagation();
                                              handleRetryWebhook(log.id);
                                            }}
                                          >
                                            Retry
                                          </Button>
                                        ),
                                      },
                                    ]
                                  : []),
                              ]}
                            />
                          )}
                        </div>
                      )}
                    </div>
                  </Col>
                )}
              </Row>
            </div>
          </Col>
        </Row>
        {!isPartnerViewer && (
          <Collapse
            ghost
            size="small"
            style={{ marginTop: 16 }}
            items={[
              {
                key: 'advanced',
                label: <span style={{ fontSize: 12, color: '#999' }}>Advanced</span>,
                children: (
                  <pre
                    style={{
                      background: '#f5f5f5',
                      padding: 16,
                      borderRadius: 4,
                      fontSize: 11,
                      maxHeight: 500,
                      overflow: 'auto',
                      whiteSpace: 'pre-wrap',
                      wordBreak: 'break-all',
                    }}
                  >
                    {JSON.stringify(order, null, 2)}
                  </pre>
                ),
              },
            ]}
          />
        )}
      </div>
      <Modal
        width={1128}
        closeIcon={false}
        title={titleModal || 'Edit Order Info'}
        open={isModalOpen}
        forceRender
        footer={false}
        onCancel={cancelModal}
      >
        <Spin spinning={isFetching}>
          <div className="OrderDetailModal">
            <Form
              form={form}
              scrollToFirstError
              layout="vertical"
              requiredMark={false}
              colon={false}
              className="FormContent"
              onFinish={onFinishForm}
              onValuesChange={onValuesChange}
            >
              {actionPage === typePage.order.type && (
                <OrderInfoForm order={order} types={types} setTypes={setTypes} partners={partners} />
              )}
              {actionPage === typePage.pickup.type && <PickupForm form={form} />}
              {actionPage === typePage.delivery.type && <DeliveryForm form={form} />}
              <div className="FormFooter">
                <Button className="ButtonCancel" onClick={cancelModal}>
                  Cancel
                </Button>
                <Button className="CreateButton ButtonPrimary" type="primary" htmlType="submit">
                  Update
                  <ReactSVG className="icon" src="/icons/done.svg" />
                </Button>
              </div>
            </Form>
          </div>
        </Spin>
      </Modal>
      <ColumnConfigDrawer
        open={fieldConfig.drawerOpen}
        onClose={fieldConfig.closeDrawer}
        columns={fieldConfig.orderedAllColumns}
        hidden={fieldConfig.hidden}
        fixedKeys={[]}
        onToggle={fieldConfig.toggleColumn}
        onMove={fieldConfig.moveColumn}
        onReset={fieldConfig.resetConfig}
        hidePageSize
      />
    </Spin>
  );
};

export default OrderDetailPage;
